import React from 'react';
import { useIntl } from 'react-intl';
import { MdBlock, MdInfoOutline, MdUndo } from 'react-icons/md';
import {
  Button,
  PromoBanner,
  Spinner,
  PROMO_BANNER_DENSITY_COMPACT,
  PROMO_BANNER_TONE_ERROR,
} from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import KitUpdateNotice from '../../appFrame/KitUpdateNotice';
import { NoticeStack, ProblemList, Problems } from './BuilderNotices.styled';

export interface BuilderNoticeState {
  readonly reviewing: boolean;
  readonly suggestionApplied: boolean;
  readonly isSavingPrompt: boolean;
  readonly undoSuggestion: () => void;
}

interface BuilderNoticesProps {
  readonly draft: UserAppDraft;
  readonly publishing: BuilderNoticeState;
  readonly problems: readonly string[];
  readonly onAskAssistant: (text: string) => void;
}

const BuilderNotices: React.FC<BuilderNoticesProps> = ({ draft, publishing, problems, onAskAssistant }) => {
  const intl = useIntl();
  return (
    <NoticeStack>
      {draft.status === 'DISABLED' && (
        <PromoBanner
          density={PROMO_BANNER_DENSITY_COMPACT}
          tone={PROMO_BANNER_TONE_ERROR}
          icon={<MdBlock />}
          title={intl.formatMessage({
            id: 'apps.builder.disabled',
            defaultMessage:
              'AIBuild has switched this app off, so nobody can run it and it cannot be published or shared. You can still edit the draft. Contact support if you think this is a mistake.',
          })}
          dataTestId="app-builder__disabled"
        />
      )}
      {publishing.reviewing && (
        <PromoBanner
          density={PROMO_BANNER_DENSITY_COMPACT}
          icon={<Spinner size="sm" />}
          title={intl.formatMessage({
            id: 'apps.builder.review.inProgress',
            defaultMessage: 'Reviewing your app before publishing. This can take up to a minute.',
          })}
          dataTestId="app-builder__reviewing"
        />
      )}
      {publishing.suggestionApplied && (
        <PromoBanner
          density={PROMO_BANNER_DENSITY_COMPACT}
          icon={<MdInfoOutline />}
          title={intl.formatMessage({
            id: 'apps.builder.review.applied',
            defaultMessage: 'The suggested prompt is in your draft. Publish again when you are ready.',
          })}
          actions={
            <Button
              variant="text"
              size="sm"
              leadingIcon={<MdUndo size={16} aria-hidden="true" />}
              loading={publishing.isSavingPrompt}
              onClick={publishing.undoSuggestion}
              dataTestId="app-builder__undo-suggestion"
            >
              {intl.formatMessage({ id: 'apps.builder.review.undo', defaultMessage: 'Undo' })}
            </Button>
          }
          dataTestId="app-builder__suggestion-applied"
        />
      )}
      <KitUpdateNotice
        appKey={draft.appKey}
        manifest={draft.preview}
        onAskAssistant={() =>
          onAskAssistant(
            intl.formatMessage({
              id: 'apps.kitUpdate.request',
              defaultMessage: "Update this app's page to the latest App Kit, following the kit's migration notes.",
            }),
          )
        }
      />
      {problems.length > 0 && (
        <Problems role="alert" data-testid="app-builder__problems">
          <ProblemList>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ProblemList>
          <Button
            variant="outlined"
            size="sm"
            onClick={() =>
              onAskAssistant(
                intl.formatMessage(
                  { id: 'apps.builder.problems.fixRequest', defaultMessage: 'The app cannot run yet; fix these problems:\n{problems}' },
                  { problems: problems.map((problem) => `- ${problem}`).join('\n') },
                ),
              )
            }
            dataTestId="app-builder__problems-fix"
          >
            {intl.formatMessage({ id: 'apps.builder.problems.fix', defaultMessage: 'Ask the assistant to fix' })}
          </Button>
        </Problems>
      )}
    </NoticeStack>
  );
};

export default BuilderNotices;
